import React from "react";
import LegalPage from "@/components/LegalPage";
import { useTranslation } from "@/lib/i18n";
import { useConsentUi } from "@/components/consent/ConsentProvider";

/**
 * Cookie Policy — canonical Legal surface.
 *
 * Slice-B/C follow-up: the "Managing cookies" section now embeds an
 * interactive "Cookie settings / Cookie-Einstellungen" control that
 * opens the SAME granular settings dialog reachable from the footer.
 * The interactive control reuses `useConsentUi().openSettings()` from
 * the shared `ConsentProvider` — no second consent implementation, no
 * change to consent categories, defaults or PostHog behavior.
 *
 * The i18n copy uses a `{cookie_settings}` placeholder so the
 * canonical legal text remains centralized; only the placeholder
 * position is substituted with an accessible <button>.
 */
export default function CookiePolicy() {
  const { t, locale } = useTranslation();
  const { openSettings } = useConsentUi();
  const cookieSettingsLabel = t("consent.footer_settings_button");
  // Canonical /legal/cookies redirects into the language-scoped WS-11
  // route in production. Pass the current app-wide locale explicitly
  // so the dialog matches the visible page language even when
  // localStorage holds a different persisted value.
  const openInPageLanguage = () => openSettings(locale);

  // Render `s4_b` around the `{cookie_settings}` marker so the legal
  // text stays canonical and only the placeholder becomes a real,
  // keyboard-accessible button.
  const s4Raw = t("legal.cookies.s4_b");
  const [s4Before, s4After] = s4Raw.includes("{cookie_settings}")
    ? s4Raw.split("{cookie_settings}")
    : [s4Raw, ""];
  const s4Body = (
    <>
      {s4Before}
      <button
        type="button"
        onClick={openInPageLanguage}
        className="inline underline underline-offset-2 decoration-primary/60 hover:decoration-primary text-primary font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded-sm"
        aria-label={cookieSettingsLabel}
        data-testid="cookies-page-open-settings"
      >
        {cookieSettingsLabel}
      </button>
      {s4After}
    </>
  );

  return (
    <LegalPage
      title={t("legal.cookies.title")}
      lastUpdated={t("legal.cookies.last_updated")}
      testId="cookies-page"
      intro={t("legal.cookies.intro")}
      sections={[
        { heading: t("legal.cookies.s1_h"), body: [t("legal.cookies.s1_b1"), t("legal.cookies.s1_b2")] },
        { heading: t("legal.cookies.s2_h"), body: [t("legal.cookies.s2_b1"), t("legal.cookies.s2_b2")] },
        { heading: t("legal.cookies.s3_h"), body: t("legal.cookies.s3_b") },
        { heading: t("legal.cookies.s4_h"), body: s4Body },
      ]}
    />
  );
}
